<template>
  <div class="dispatch-modal tw-bg-transparent tw-shadow-none tw-border-0 tw-rounded-none tw-overflow-hidden">
    <template v-if="$apollo.queries.container.loading">
      <div class="tw-border-b tw-border-slate-200/70 tw-px-4 tw-py-4 sm:tw-px-5">
        <div class="tw-flex tw-items-center tw-justify-between">
          <div class="tw-flex tw-items-center tw-gap-2">
            <font-awesome-icon icon="circle-notch" spin class="tw-h-4 tw-w-4 tw-text-slate-500" />
            <span class="tw-text-sm tw-font-semibold tw-text-slate-700">Loading container…</span>
          </div>
          <div class="tw-h-9 tw-w-9 tw-rounded-lg tw-border tw-border-slate-200 tw-bg-slate-50"></div>
        </div>
        <div class="tw-mt-3 tw-h-3 tw-w-40 tw-rounded tw-bg-slate-100"></div>
      </div>

      <div class="tw-p-4 sm:tw-p-5">
        <div class="tw-animate-pulse tw-space-y-4">
          <div class="tw-grid tw-grid-cols-1 sm:tw-grid-cols-3 tw-gap-3">
            <div class="tw-h-20 tw-rounded-xl tw-border tw-border-slate-200/70 tw-bg-white/60"></div>
            <div class="tw-h-20 tw-rounded-xl tw-border tw-border-slate-200/70 tw-bg-white/60"></div>
            <div class="tw-h-20 tw-rounded-xl tw-border tw-border-slate-200/70 tw-bg-white/60"></div>
          </div>

          <div class="tw-h-10 tw-rounded-xl tw-bg-slate-100"></div>

          <div class="tw-rounded-2xl tw-border tw-border-slate-200/70 tw-overflow-hidden">
            <div class="tw-h-10 tw-bg-slate-50"></div>
            <div class="tw-space-y-0">
              <div class="tw-h-10 tw-bg-white"></div>
              <div class="tw-h-10 tw-bg-slate-50/50"></div>
              <div class="tw-h-10 tw-bg-white"></div>
            </div>
          </div>
        </div>
      </div>
    </template>

    <template v-else-if="container">
      <div class="tw-border-b tw-border-slate-200/70 tw-bg-white/80 tw-px-4 tw-py-4 sm:tw-px-5">
        <div class="tw-flex tw-flex-col tw-gap-3 sm:tw-flex-row sm:tw-items-start sm:tw-justify-between">
          <div class="tw-min-w-0">
            <div class="tw-flex tw-flex-wrap tw-items-center tw-gap-2">
              <span
                v-if="container.order && container.order.isExport"
                class="tw-inline-flex tw-items-center tw-rounded-md tw-bg-amber-500/15 tw-px-2.5 tw-py-1 tw-text-xs tw-font-semibold tw-text-amber-800"
              >Export</span>
              <span
                v-else
                class="tw-inline-flex tw-items-center tw-rounded-md tw-bg-indigo-500/15 tw-px-2.5 tw-py-1 tw-text-xs tw-font-semibold tw-text-indigo-800"
              >Import</span>

              <span class="tw-text-sm tw-font-medium tw-text-slate-700">{{ container.size }}{{ container.type }}</span>
            </div>

            <div class="tw-mt-3 tw-w-full tw-overflow-x-auto">
              <div class="tw-inline-flex tw-min-w-max tw-items-stretch tw-rounded-xl tw-border tw-border-slate-200/70 tw-bg-white/60 tw-shadow-sm tw-shadow-slate-900/5 tw-divide-x tw-divide-slate-200/70">
                <div class="tw-flex tw-items-center tw-gap-2 tw-px-3 tw-py-2">
                  <span class="tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wide tw-text-slate-500">Order</span>
                  <router-link
                    v-if="container.order"
                    class="tw-text-sm tw-font-semibold tw-text-slate-900 hover:tw-underline"
                    :to="'/orders/' + container.order.id"
                  >#{{ container.order.id }}</router-link>
                  <CopyButton
                    v-if="container.order"
                    :value="String(container.order.id)"
                    title="Copy order #"
                    aria-label="Copy order number"
                  />
                </div>

                <div class="tw-flex tw-items-center tw-gap-2 tw-px-3 tw-py-2">
                  <span class="tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wide tw-text-slate-500">Container</span>
                  <span class="tw-text-sm tw-font-semibold tw-text-slate-900">
                    {{ container.containerId !== 'EXPUNDEF' ? container.containerId : 'Unknown' }}
                  </span>
                  <CopyButton
                    v-if="container.containerId && container.containerId !== 'EXPUNDEF'"
                    :value="String(container.containerId)"
                    title="Copy container #"
                    aria-label="Copy container number"
                  />
                </div>

                <div
                  v-if="container.order && container.order.isExport"
                  class="tw-flex tw-items-center tw-gap-2 tw-px-3 tw-py-2"
                >
                  <span class="tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wide tw-text-slate-500">Booking</span>
                  <span class="tw-text-sm tw-font-semibold tw-text-slate-900">{{ container.order.bookingNumber || '—' }}</span>
                  <CopyButton
                    v-if="container.order.bookingNumber"
                    :value="String(container.order.bookingNumber)"
                    title="Copy booking #"
                    aria-label="Copy booking number"
                  />
                </div>

                <div
                  v-else-if="container.order && container.order.billOfLading"
                  class="tw-flex tw-items-center tw-gap-2 tw-px-3 tw-py-2"
                >
                  <span class="tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wide tw-text-slate-500">BL</span>
                  <span class="tw-text-sm tw-font-semibold tw-text-slate-900">{{ container.order.billOfLading.billOfLadingId }}</span>
                  <CopyButton
                    v-if="container.order.billOfLading.billOfLadingId"
                    :value="String(container.order.billOfLading.billOfLadingId)"
                    title="Copy bill of lading"
                    aria-label="Copy bill of lading"
                  />
                </div>
              </div>
            </div>

            <div class="tw-mt-3 tw-flex tw-flex-wrap tw-items-end tw-gap-x-4 tw-gap-y-1">
              <div class="tw-flex tw-flex-wrap tw-items-center tw-gap-2 tw-text-sm tw-text-slate-600">
                <span v-if="container.terminalAppointmentDate" class="tw-inline-flex tw-items-center tw-rounded-full tw-border tw-border-slate-200/70 tw-bg-white tw-px-2 tw-py-0.5 tw-text-xs tw-font-medium tw-text-slate-700">
                  Term Apt: {{ formatAppointmentDatetime(container.terminalAppointmentDate, container.terminalAppointmentHasTime) }}
                </span>
                <span v-if="container.deliveryAppointmentDate" class="tw-inline-flex tw-items-center tw-rounded-full tw-border tw-border-slate-200/70 tw-bg-white tw-px-2 tw-py-0.5 tw-text-xs tw-font-medium tw-text-slate-700">
                  Del Apt: {{ formatAppointmentDatetime(container.deliveryAppointmentDate, container.deliveryAppointmentHasTime) }}
                </span>
              </div>
            </div>
          </div>

        </div>

        <div v-if="tripStops.length > 1" class="tw-mt-4">
          <div class="tw-relative tw-mt-2">
            <div class="tw-relative tw-h-6">
              <div class="tw-absolute tw-left-0 tw-right-0 tw-top-1/2 tw-h-1 tw--translate-y-1/2 tw-rounded-full tw-bg-slate-200"></div>
              <div
                class="tw-absolute tw-left-0 tw-top-1/2 tw-h-1 tw--translate-y-1/2 tw-rounded-full tw-bg-emerald-500"
                :style="{ width: tripProgressPercent + '%' }"
              ></div>

              <div class="tw-absolute tw-inset-x-0 tw-top-1/2 tw-flex tw--translate-y-1/2 tw-items-center tw-justify-between">
                <div
                  v-for="(stop, idx) in tripStops"
                  :key="`stop-dot-${idx}`"
                  class="tw-flex tw-items-center tw-justify-center"
                  :title="stop"
                >
                  <div
                    class="tw-relative tw-z-10 tw-h-3.5 tw-w-3.5 tw-rounded-full tw-border tw-bg-white"
                    :class="idx <= tripProgressStopIndex
                      ? 'tw-border-emerald-500 tw-bg-emerald-500'
                      : 'tw-border-slate-300'"
                  ></div>
                </div>
              </div>
            </div>

            <div class="tw-mt-2 tw-flex tw-items-start tw-justify-between tw-gap-2">
              <div
                v-for="(stop, idx) in tripStops"
                :key="`stop-label-${idx}`"
                class="tw-w-20 tw-text-center tw-text-[11px] tw-leading-tight tw-text-slate-500 tw-truncate"
                :title="stop"
              >
                {{ stop }}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="tw-p-4 sm:tw-p-5">
        <div class="tw-flex tw-justify-center">
          <ul
            class="uk-subnav uk-subnav-pill uk-margin-remove-bottom dispatch-modal__tabs"
            uk-switcher="connect: .switcher-container"
          >
            <li>
              <a href=""> Trips </a>
            </li>
            <li>
              <a href="">Order</a>
            </li>
            <li>
              <a href="">Containers</a>
            </li>
          </ul>
        </div>

        <ul class="uk-switcher switcher-container uk-margin">
          <li>
            <ContainerTripsTable
              caller="dispatch"
              :order="container.order"
              :container="container"
              :trips="container.trips"
              :drivers="drivers"
              :chassis="filteredChassis"
              :editable="!container.isCancelled"
              @refreshOrders="emitDispatchRefresh"
            />
          </li>
          <li>
            <div>
              <DispatchOrderCard :orderId="Number(container.order.id)">
              </DispatchOrderCard>
            </div>
          </li>
          <li>
            <div>
              <DispatchContainerInfo :container="container">
              </DispatchContainerInfo>
            </div>
          </li>
        </ul>
      </div>
    </template>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import DispatchContainerQuery from '@/gql/dispatch/query/DispatchContainer.gql';
import DispatchOrderCard from './DispatchOrderCard.vue';
import DispatchContainerInfo from './DispatchContainerInfo.vue';
import ContainerTripsTable from '../Orders/ContainerTripsTable.vue';
import CopyButton from '@/components/core/CopyButton.vue';

import { yardLocations } from '../../utils/locations';
import { dateFormatMixin, datetimeFormatMixin } from '../../utils/date';

export default defineComponent({
  props: {
    containerId: {
      default: -1,
    },
    drivers: {
      type: Array,
    },
    filteredChassis: {
      default: () => [],
    },
  },
  emits: ['close', 'dispachContainerRefetch'],
  components: {
    ContainerTripsTable,
    DispatchOrderCard,
    DispatchContainerInfo,
    CopyButton,
  },
  data(): any {
    return {
      // trips
      loading: false,
      yardLocations,
      newChassis: [],
      //   isAddingTrip: false,
    };
  },
  mixins: [datetimeFormatMixin],
  computed: {
    sortedTrips(): any[] {
      const vm = (this as any);
      const trips = (vm.container && vm.container.trips) ? vm.container.trips : [];
      return trips.slice().sort((a: any, b: any) => Number(a.legId) - Number(b.legId));
    },
    tripStops(): string[] {
      const vm = (this as any);
      const trips = vm.sortedTrips || [];
      if (trips.length === 0) return [];

      const toLabel = (loc: any): string => {
        if (!loc) return '';
        if (loc.shortName) return String(loc.shortName);
        if (loc.name) return String(loc.name);
        return '';
      };

      const origin = toLabel(trips[0].origin);
      const destinations = trips.map((t: any) => toLabel(t.destination)).filter((x: string) => !!x);
      const stops = [origin, ...destinations].filter((x) => !!x);

      // Remove immediate duplicates (same location repeated)
      return stops.filter((val, idx, arr) => idx === 0 || val !== arr[idx - 1]);
    },
    tripProgressStopIndex(): number {
      const vm = (this as any);
      const trips = vm.sortedTrips || [];
      const stops = vm.tripStops || [];
      if (trips.length === 0 || stops.length <= 1) return 0;

      const completed = trips.filter((t: any) => t && t.status === 'Complete').length;
      return Math.min(completed, stops.length - 1);
    },
    tripProgressPercent(): number {
      const vm = (this as any);
      const trips = vm.sortedTrips || [];
      const stops = vm.tripStops || [];
      if (stops.length <= 1) return 0;

      const completed = trips.filter((t: any) => t && t.status === 'Complete').length;
      const inProgress = trips.some((t: any) => t && t.status === 'In Progress');
      const denom = Math.max(1, stops.length - 1);
      const numerator = Math.min(denom, completed + (inProgress ? 0.5 : 0));
      return Math.max(0, Math.min(100, (numerator / denom) * 100));
    },
  },
  methods: {
    emitDispatchRefresh() {
      (this as any).$emit('dispachContainerRefetch');
    },
  },
  ...{
    apollo: {
      container: {
        query: DispatchContainerQuery,
        variables() {
          return {
            id: (this as any).containerId,
          };
        },
        skip() {
          return (this as any).containerId === -1;
        },
      },
    },
  },
});
</script>

<style lang="scss" scoped>
.uk-table-striped > tr:nth-of-type(odd),
.uk-table-striped tbody tr:nth-of-type(odd) .activeContainerRow {
  background: #6c7c83;
  color: #ffffff;
}
tr.activeContainerRow {
  background: #6c7c83 !important;
  color: #ffffff;
}

/* Backdrop: frosted/blurred, modal content remains opaque */
.dispatch-modal {
  background: transparent;
  backdrop-filter: none;
}

/* Dialog sizing + remove UIkit muted background so Tailwind can fully control it */
.dispatch-modal__dialog {
  width: min(1100px, calc(100vw - 2rem));
  margin: 1rem auto;
  border-radius: 1rem;
  background: transparent;
}

/* Tabs: fully restyle into a segmented control */
.dispatch-modal :deep(.dispatch-modal__tabs) {
  display: inline-flex;
  align-items: stretch;
  gap: 0;
  padding: 0.25rem;
  border-radius: 0.9rem;
  border: 1px solid rgba(226, 232, 240, 0.9);
  background: rgba(15, 23, 42, 0.04);
}

.dispatch-modal :deep(.dispatch-modal__tabs > li) {
  margin: 0;
  padding: 0;
}

.dispatch-modal :deep(.dispatch-modal__tabs > li > a) {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 2.25rem;
  padding: 0 0.95rem;
  border-radius: 0.75rem;
  border: 1px solid transparent;
  background: transparent;
  color: rgba(71, 85, 105, 1);
  font-size: 0.875rem;
  font-weight: 700;
  letter-spacing: -0.01em;
  transition: background 120ms ease, color 120ms ease, box-shadow 120ms ease;
}

.dispatch-modal :deep(.dispatch-modal__tabs > li.uk-active > a) {
  background: rgba(255, 255, 255, 1);
  color: rgba(15, 23, 42, 1);
  box-shadow: 0 8px 18px rgba(15, 23, 42, 0.08);
}

.dispatch-modal :deep(.dispatch-modal__tabs > li:not(.uk-active) > a:hover) {
  background: rgba(255, 255, 255, 0.6);
  color: rgba(15, 23, 42, 1);
}
</style>
